'use client';

import React, { useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { CheckIcon, XMarkIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import InternationalCountryModal from './components/InternationalCountryModal';
import CountryFlag from './components/CountryFlag';
import { formatInternationalPhoneNumber } from './utils/countryUtils';

export default function PhoneVerificationModal({ uid, onVerified, onClose }) {
  const [phone, setPhone] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState("");
  const [selectedCountry, setSelectedCountry] = useState({ code: 'US', name: 'United States', dialingCode: '+1' });
  const [showCountryModal, setShowCountryModal] = useState(false);

  // Format the phone number with the selected country
  const formatPhoneNumber = (input) => {
    if (!input) return '';
    
    // Use the international formatter with selected country
    return formatInternationalPhoneNumber(input, selectedCountry?.code);
  };

  const fullPhone = formatPhoneNumber(phone);

  // Handle phone input changes
  const handlePhoneChange = (e) => {
    let value = e.target.value;
    
    // Remove any existing country code if user types it (the country selector handles this)
    if (value.startsWith(selectedCountry.dialingCode)) {
      value = value.substring(selectedCountry.dialingCode.length);
    } else if (value.startsWith('+')) {
      // If they start typing +, remove it (country selector shows the code)
      value = value.substring(1);
    }
    
    setPhone(value);
  };

  // Handle country selection
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowCountryModal(false);
    
    // If there's already a number entered, keep it as is since it's now local to the selected country
    // The formatPhoneNumber function will add the correct country code
  };

  // Send verification code via Telnyx
  const sendCode = async () => {
    setIsSending(true);
    setError("");
    try {
      console.log("Sending verification to:", fullPhone); // Debug log
      const res = await fetch("/api/verify/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: fullPhone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send code");
      setCodeSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSending(false);
    }
  };

  // Verify the code and update the user's Firestore document
  const verifyCode = async () => {
    setIsVerifying(true);
    setError("");
    try {
      console.log("Verifying code for:", fullPhone); // Debug log
      // Verify the code with Telnyx
      const res = await fetch("/api/verify/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phoneNumber: fullPhone,
          verificationCode: code,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Verification failed");

      // If Telnyx accepts the code, update the Firestore document.
      // uid is included in the payload so that the API knows which document to update.
      if (data.data?.response_code === "accepted") {
        const updateRes = await fetch("/api/verify/updateForwardToNumber", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uid, forwardToNumber: fullPhone }),
        });
        const updateData = await updateRes.json();
        if (!updateRes.ok) {
          throw new Error(updateData.error || "Failed to update forwardToNumber");
        }
        setVerified(true);
        onVerified(fullPhone);
      } else {
        throw new Error("Verification not accepted");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel 
          className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-black/20 backdrop-blur-2xl border border-white/10 p-8 text-left shadow-xl transition-all"
          style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.3)'}}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white/80 focus:outline-none p-2 rounded-lg hover:bg-white/5 transition-all duration-200"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
          
          <DialogTitle as="h3" className="text-2xl font-light leading-6 text-white mb-2">
            Verify Your Phone
          </DialogTitle>
          <p className="text-sm text-white/70 mb-6">
            We need to verify your phone number to enable call and text forwarding to your device.
          </p>

          {!codeSent ? (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-3">Phone Number</label>
                
                {/* Country Selection */}
                <div className="mb-3">
                  <button
                    type="button"
                    onClick={() => setShowCountryModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white/80 hover:bg-white/10 hover:text-white transition-all duration-200"
                  >
                    <CountryFlag countryCode={selectedCountry.code} size="sm" />
                    <span className="text-sm">{selectedCountry.name}</span>
                    <span className="text-xs text-white/60">{selectedCountry.dialingCode}</span>
                  </button>
                </div>

                <div className="flex">
                  <span className="inline-flex items-center rounded-l-xl border border-r-0 border-white/20 bg-white/5 backdrop-blur px-4 text-white/70 font-mono">
                    {selectedCountry.dialingCode}
                  </span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isSending && phone.trim() !== "") {
                        e.preventDefault();
                        sendCode();
                      }
                    }}
                    placeholder={selectedCountry.code === 'US' ? "415 999 8941" : "Enter phone number"}
                    className="block w-full flex-1 rounded-r-xl border border-white/20 bg-white/5 backdrop-blur px-4 py-3 text-white placeholder-white/50 focus:border-indigo-400/50 focus:outline-none focus:ring-2 focus:ring-indigo-400/20 transition-all duration-200"
                  />
                </div>
                {phone && (
                  <p className="mt-2 text-xs text-white/60">
                    Will send to: <span className="font-mono text-indigo-300">{fullPhone}</span>
                  </p>
                )}
              </div>
              
              <button
                type="button"
                onClick={sendCode}
                disabled={isSending || phone.trim() === ""}
                className="w-full rounded-xl bg-indigo-500/20 backdrop-blur border border-indigo-400/30 px-6 py-4 text-sm font-medium text-white shadow hover:bg-indigo-500/30 focus:outline-none focus:ring-2 focus:ring-indigo-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105"
                style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(99, 102, 241, 0.2)'}}
              >
                {isSending ? (
                  <div className="flex items-center justify-center">
                    <svg className="h-5 w-5 animate-spin mr-3" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                      <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" className="opacity-75" />
                    </svg>
                    Sending verification code...
                  </div>
                ) : (
                  "Send Verification Code"
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-3">Verification Code</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isVerifying && code.trim() !== "") {
                      e.preventDefault();
                      verifyCode();
                    }
                  }}
                                     placeholder="Enter 5-digit code"
                  className="block w-full rounded-xl border border-white/20 bg-white/5 backdrop-blur px-4 py-3 text-white placeholder-white/50 focus:border-indigo-400/50 focus:outline-none focus:ring-2 focus:ring-indigo-400/20 transition-all duration-200 text-center tracking-widest font-mono"
                />
                <p className="mt-2 text-xs text-white/60">
                  Code sent to: <span className="font-mono text-indigo-300">{fullPhone}</span>
                </p>
              </div>
              
              <button
                type="button"
                onClick={verifyCode}
                disabled={isVerifying || code.trim() === ""}
                className="w-full inline-flex items-center justify-center gap-3 rounded-xl bg-indigo-500/20 backdrop-blur border border-indigo-400/30 px-6 py-4 text-sm font-medium text-white shadow hover:bg-indigo-500/30 focus:outline-none focus:ring-2 focus:ring-indigo-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105"
                style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(99, 102, 241, 0.2)'}}
              >
                {isVerifying ? (
                  <>
                    <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                      <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" className="opacity-75" />
                    </svg>
                    Verifying...
                  </>
                ) : verified ? (
                  <>
                    <CheckIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
                    Verified Successfully
                  </>
                ) : (
                  "Verify Code"
                )}
              </button>
            </div>
          )}
          
          {error && (
            <div className="mt-4 rounded-xl bg-red-500/10 border border-red-500/20 p-4">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </DialogPanel>
      </div>
      
      {/* International Country Selection Modal */}
      <InternationalCountryModal
        isOpen={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        onSelectCountry={handleCountrySelect}
      />
    </Dialog>
  );
}